// src/pages/HomePage.js

import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Carousel, Button } from 'react-bootstrap';
import ContactForm from '../components/ContactForm';
import SeoMeta from '../components/SeoMeta';
import sanityClient from '../sanity';
import '../styles/HomePage.css';
import '../styles/ContactCard.css';
import { useNavigate } from 'react-router-dom';

function HomePage() {
    const [news, setNews] = useState([]);
    const [welcomeVisible, setWelcomeVisible] = useState(false);
    const welcomeRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        sanityClient
            .fetch(`*[_type == "news"]{title, content, publishedAt, "imageUrl": image.asset->url}`)
            .then((data) => setNews(data))
            .catch(console.error);
    }, []);

    useEffect(() => {
        const welcomeBlock = welcomeRef.current;
        if (!welcomeBlock) return;

        if (!('IntersectionObserver' in window)) {
            setWelcomeVisible(true);
            return;
        }

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setWelcomeVisible(true);
                observer.disconnect();
            }
        }, { threshold: 0.25 });

        observer.observe(welcomeBlock);
        return () => observer.disconnect();
    }, []);

    const truncateContent = (content) => {
        if (content.length > 200) {
            return content.substring(0, 200) + '...';
        }
        return content;
    };

    const handleReadMore = (index) => {
        navigate(`/news/${index}`);
    };

    return (
        <div className="homepage-container">
            <SeoMeta
                title="Ziyad Attorneys | Law Office in Addis Ababa, Ethiopia"
                description="Ziyad Attorneys provides legal representation and professional legal services in Addis Ababa, Ethiopia. Contact our office to discuss your legal needs."
            />
            <Carousel className="professional-carousel" controls indicators>
                <Carousel.Item>
                    <div className="carousel-image-container">
                        <img
                            className="d-block w-100 carousel-image"
                            src="bigrum.jpg"
                            alt="Legal expertise at Ziyad Attorneys"
                        />
                    </div>
                    <Carousel.Caption>
                        <h3><span className="carousel-title-accent">Legal</span> Representation</h3>
                        <p>Trusted advocacy for over a decade in Ethiopia's judicial system</p>
                    </Carousel.Caption>
                </Carousel.Item>
                <Carousel.Item>
                    <div className="carousel-image-container">
                        <img
                            className="d-block w-100 carousel-image"
                            src="o1p.png"
                            alt="Dedicated legal team at Ziyad Attorneys"
                        />
                    </div>
                    <Carousel.Caption>
                        <h3><span className="carousel-title-accent">Dedicated</span> Legal Team</h3>
                        <p>Highly skilled professionals across multiple practice areas</p>
                    </Carousel.Caption>
                </Carousel.Item>
                <Carousel.Item>
                    <div className="carousel-image-container">
                        <img
                            className="d-block w-100 carousel-image"
                            src="o5p.png"
                            alt="Justice and truth at Ziyad Attorneys"
                        />
                    </div>
                    <Carousel.Caption>
                        <h3><span className="carousel-title-accent">Justice</span> &amp; Truth</h3>
                        <p>Guided by our core principle: "Truth Wins"</p>
                    </Carousel.Caption>
                </Carousel.Item>
            </Carousel>

            <Container className="my-5 container-custom">
                <div className="header-section">
                    <div
                        ref={welcomeRef}
                        className={`header-card${welcomeVisible ? ' header-card--visible' : ''}`}
                    >
                        <h1>Welcome to <span className="firm-name">Ziyad Attorneys</span></h1>
                        <p className="header-subtitle">Excellence in Legal Representation Since 2014</p>
                    </div>
                </div>
                <Row className="about-section">
                    <Col md={4} className="mb-4">
                        <Card className="h-100 shadow-sm professional-card">
                            <div className="card-image-container">
                                <Card.Img variant="top" src="zk11p.png" alt="Ziyad Redwan, Founder" />
                                <div className="card-overlay">
                                    <Button variant="outline-light">View Profile</Button>
                                </div>
                            </div>
                            <Card.Body className="text-center">
                                <Card.Title>Ziyad Redwan, Founder</Card.Title>
                                <Card.Text>LLB, LLM, PhD Candidate with 17+ years of experience in Ethiopia's legal sector</Card.Text>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={8}>
                        <div className="about-content">
                            <h2>About Our Firm</h2>
                            <h5>For the Best Interest of the Client</h5>
                            <p>
                                At Ziyad Attorneys, our primary focus is you. For more than 10 years, we have been privileged to serve the legal needs 
                                of our clients in Ethiopia, guided by a single, unwavering principle. Our motto, <em>"the best interest of the clients,"</em> is the 
                                foundation upon which we operate and the core of every strategy we develop and every action we take. Your goals are our goals, 
                                and we are dedicated to achieving the most favorable outcomes for you.
                            </p>
                            <h5>A Supportive Partnership</h5>
                            <p>
                                We understand that navigating the legal system can be a complex and challenging experience. Our team is committed to providing not only 
                                expert legal counsel but also a reliable and supportive partnership you can depend on. We stand with you at every step, offering clear 
                                communication and trusted guidance to help you make informed decisions. We aim to be more than just your lawyers; we strive to be your
                                steadfast allies on your legal journey.
                            </p>
                            <h5>Reputable Brand in Legal Services</h5>
                            <p>
                                Our decade of dedicated service have allowed us to build a unique and respected brand within the Ethiopian legal community. This reputation 
                                for integrity and excellence has been earned through consistent results and an unwavering commitment to justice. We pride ourselves on our 
                                ability to adapt to the ever-changing legal environment, ensuring our clients always receive modern, relevant, and effective representation. 
                                Trust in our experience and our reputable brand to protect your interests. We are your legal protection plus business asset.                                
                            </p>
                            <Button variant="primary" className="mt-3" onClick={() => {window.scrollTo(0, 0); navigate("/services"); }}>Learn More About Our Practice</Button>
                        </div>
                    </Col>
                </Row>
            </Container>

            <Container className="my-5 news-section">
                <h2 className="section-title">Latest News & Insights</h2>
                <Row>
                    {news.length > 0 ? (
                        news.map((item, index) => (
                            <Col key={index} md={4} className="mb-4">
                                <Card className="h-100 shadow-sm professional-card">
                                    {item.imageUrl && (
                                        <div className="card-image-container">
                                            <Card.Img
                                                variant="top"
                                                src={item.imageUrl}
                                                alt={item.title}
                                                className="news-image"
                                            />
                                        </div>
                                    )}
                                    <Card.Body>
                                        <Card.Title>{item.title}</Card.Title>
                                        <Card.Text>{truncateContent(item.content)}</Card.Text>
                                        <Button variant="outline-primary" onClick={() => handleReadMore(index)}>
                                            Read More
                                        </Button>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))
                    ) : (
                        <Col>
                            <Card className="h-100 shadow-sm professional-card">
                                <Card.Body className="text-center">
                                    <Card.Title>No News Available</Card.Title>
                                    <Card.Text>There are currently no news items to display. Please check back later.</Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                    )}
                </Row>
            </Container>
            
            <Container className="my-5 contact-section">
                <h2 className="section-title">Contact Us</h2>
                <Row>
                    <Col md={6}>
                        <Card className="h-100 shadow-sm professional-card contact-card contact-info-card">
                            <Card.Body>
                                <Card.Title>Contact Information</Card.Title>
                                <div className="contact-item">
                                    <i className="fas fa-map-marker-alt"></i>
                                    <span>Addis Ababa, Ethiopia</span>
                                </div>
                                <div className="contact-item">
                                    <i className="fas fa-phone"></i>
                                    <span className="contact-phone-number">
                                        <a href="tel:+251913811743">+251-913-81-17-43</a>
                                        {' / '}
                                        <a href="tel:+251930750575">+251-930-75-05-75</a>
                                    </span>
                                </div>
                                <div className="contact-item">
                                    <i className="fas fa-envelope"></i>
                                    <span>ziyadlawoffice@gmail.com</span>
                                </div>
                                
                                <div className="business-hours mt-4">
                                    <Card.Title>Business Hours</Card.Title>
                                    <p className="business-hours-row">Monday - Friday: 8:30 AM - 5:30 PM</p>
                                    <p className="business-hours-row">Saturday: 9:00 AM - 1:00 PM</p>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={6}>
                        <div className="map-container">
                            <img src="o1cp.png" alt="Office location" className="img-fluid rounded office-image" />
                        </div>
                    </Col>
                </Row>
            </Container>            

            <Container className="my-5 location-section">
                <h2 className="section-title">Our Location</h2>
                <div className="map-wrapper">
                    <iframe 
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3940.6172884450602!2d38.71627910948994!3d9.00731778929289!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x164b85cef5ab402d%3A0x4ab1b61f8cd4199c!2s2P49%2BWGH%2C%20Addis%20Ababa%2C%20Ethiopia!5e0!3m2!1sen!2sse!4v1754667596642!5m2!1sen!2sse" 
                        width="100%" 
                        height="450" 
                        style={{ border: 0 }} 
                        allowFullScreen="" 
                        loading="lazy" 
                        referrerPolicy="no-referrer-when-downgrade"
                        title="Ziad Redwan Law Office Location"
                    >
                    </iframe>
                </div>
            </Container>

            <Container className="my-5 message-section">
                <h2 className="section-title">Send Us a Message</h2>
                <Row>
                    <Col md={8} className="mx-auto">
                        <ContactForm />
                    </Col>
                </Row>
            </Container>
        </div>
    );
}

export default HomePage;
